import Phaser from 'phaser';
import { LANE_COUNT, TILE } from '../../sim/constants';
import { T_SOLID, hotAt, tileAt, type EnemyKind } from '../../sim/room';
import type { Enemy, Simulation } from '../../sim/simulation';
import { MAX_GAME_WIDTH } from '../layout';
import { LANE_PAD_BOTTOM, LANE_PAD_X, paintLane, paintSheet } from './art';
import type { Biome } from './biomes';
import { DEPTH, depthScale, ease, laneAlpha } from './depth';
import { LANE_HAZE, RADIATION, hexNumber, mix } from './palette';
import {
  BEAM_SHEET,
  BOLT_SHEET,
  CACHE_SHEET,
  CELL_SHEET,
  CRAWLER_SHEET,
  DAZE_SHEET,
  DRONE_FRAME,
  DRONE_SHEET,
  LASER_SHEET,
  PLAYER_FRAME,
  PLAYER_SHEET,
  RANGER_SHEET,
  ROCKET_SHEET,
  SHARD_SHEET,
  type SpriteSheet,
} from './sprites';

const TEX = {
  sky: 'sky',
  far: 'far',
  mid: 'mid',
  foreground: 'foreground',
  player: 'player',
  crawler: 'crawler',
  drone: 'drone',
  ranger: 'ranger',
  bolt: 'bolt',
  daze: 'daze',
  cell: 'cell',
  shard: 'shard',
  laser: 'laser',
  beam: 'beam',
  rocket: 'rocket',
  cache: 'cache',
  lane: (i: number) => `lane-${i}`,
};

const SCENERY_HEIGHT = 270;

/** Paints every texture the world uses. Call once per room before creating a WorldView. */
export function createWorldTextures(scene: Phaser.Scene, sim: Simulation, biome: Biome): void {
  const canvas = (
    key: string,
    w: number,
    h: number,
    paint: (ctx: CanvasRenderingContext2D) => void,
  ) => {
    if (scene.textures.exists(key)) scene.textures.remove(key);
    const tex = scene.textures.createCanvas(key, w, h);
    if (!tex) throw new Error(`Could not create texture ${key}`);
    paint(tex.context);
    tex.refresh();
    return tex;
  };
  const sheet = (key: string, s: SpriteSheet) => {
    const tex = canvas(key, s.width * s.frames.length, s.height, (ctx) => paintSheet(ctx, s));
    s.frames.forEach((_, i) => tex.add(i, 0, i * s.width, 0, s.width, s.height));
  };

  canvas(TEX.sky, MAX_GAME_WIDTH, SCENERY_HEIGHT, (ctx) =>
    biome.sky(ctx, MAX_GAME_WIDTH, SCENERY_HEIGHT),
  );
  canvas(TEX.far, 512, SCENERY_HEIGHT, (ctx) => biome.far(ctx, 512, SCENERY_HEIGHT));
  canvas(TEX.mid, 384, SCENERY_HEIGHT, (ctx) => biome.mid(ctx, 384, SCENERY_HEIGHT));
  canvas(TEX.foreground, 640, SCENERY_HEIGHT, (ctx) => biome.foreground(ctx, 640, SCENERY_HEIGHT));
  const { room } = sim;
  for (let i = 0; i < LANE_COUNT; i++) {
    canvas(TEX.lane(i), room.width + LANE_PAD_X * 2, room.height + LANE_PAD_BOTTOM, (ctx) =>
      paintLane(ctx, room, i, LANE_HAZE[i] ?? 0, biome.ground, biome.haze),
    );
  }
  sheet(TEX.player, PLAYER_SHEET);
  sheet(TEX.crawler, {
    ...CRAWLER_SHEET,
    palette: { ...CRAWLER_SHEET.palette, ...biome.crawler },
  });
  sheet(TEX.drone, DRONE_SHEET);
  sheet(TEX.ranger, RANGER_SHEET);
  sheet(TEX.bolt, BOLT_SHEET);
  sheet(TEX.daze, DAZE_SHEET);
  sheet(TEX.cell, CELL_SHEET);
  sheet(TEX.shard, SHARD_SHEET);
  sheet(TEX.laser, LASER_SHEET);
  sheet(TEX.beam, BEAM_SHEET);
  sheet(TEX.rocket, ROCKET_SHEET);
  sheet(TEX.cache, CACHE_SHEET);
}

/** Phaser draw order (higher is in front). Lanes sit between the scenery and the foreground. */
const ORDER = { sky: 0, far: 1, mid: 2, laneBase: 100, foreground: 200 };
const laneOrder = (z: number) => ORDER.laneBase - z * 10;

const ENEMY_TEXTURE: Readonly<Record<EnemyKind, string>> = {
  crawler: TEX.crawler,
  drone: TEX.drone,
  ranger: TEX.ranger,
};

/** The Ranger's walk cycle: the player's frames, since it wears the same suit. */
const RUN_CYCLE = [
  PLAYER_FRAME.stride,
  PLAYER_FRAME.pass,
  PLAYER_FRAME.stride2,
  PLAYER_FRAME.pass,
] as const;

/** Radiation the suit isn't shielded for glows a sickly green; zones it is shielded for, faint cyan. */
const ZONE_COLOR = { hot: hexNumber(RADIATION.hot), safe: hexNumber(RADIATION.safe) } as const;

/** A row of hot tiles side by side, in tiles. */
interface ZoneRun {
  readonly lane: number;
  readonly tx: number;
  readonly ty: number;
  readonly length: number;
}

/** The room's radiation as runs of tiles, so a zone draws as a few rectangles. */
function zoneRuns(sim: Simulation): ZoneRun[] {
  const { room } = sim;
  const runs: ZoneRun[] = [];
  for (let lane = 0; lane < LANE_COUNT; lane++) {
    for (let ty = 0; ty < room.rows; ty++) {
      let tx = 0;
      while (tx < room.cols) {
        if (!hotAt(room, lane, tx, ty)) {
          tx++;
          continue;
        }
        const start = tx;
        while (tx < room.cols && hotAt(room, lane, tx, ty)) tx++;
        runs.push({ lane, tx: start, ty, length: tx - start });
      }
    }
  }
  return runs;
}

/** One tile side of a zone that faces open air: where its outline is drawn, in world pixels. */
interface ZoneEdge {
  readonly lane: number;
  readonly x: number;
  readonly y: number;
  readonly horizontal: boolean;
}

/** The zones' outline: every hot tile's side that faces air outside the zone (not ground). */
function zoneEdges(sim: Simulation): ZoneEdge[] {
  const { room } = sim;
  const edges: ZoneEdge[] = [];
  const open = (lane: number, tx: number, ty: number) =>
    !hotAt(room, lane, tx, ty) && tileAt(room, lane, tx, ty) !== T_SOLID;
  for (const run of zoneRuns(sim)) {
    for (let tx = run.tx; tx < run.tx + run.length; tx++) {
      const { lane, ty } = run;
      const x = tx * TILE;
      const y = ty * TILE;
      if (open(lane, tx, ty - 1)) edges.push({ lane, x, y, horizontal: true });
      if (open(lane, tx, ty + 1)) edges.push({ lane, x, y: y + TILE - 1, horizontal: true });
      if (open(lane, tx - 1, ty)) edges.push({ lane, x, y, horizontal: false });
      if (open(lane, tx + 1, ty)) edges.push({ lane, x: x + TILE - 1, y, horizontal: false });
    }
  }
  return edges;
}

/**
 * Draws the simulation. Owns the camera: an x/y position in world pixels and a depth (camZ) that
 * follows the player's lane. Every layer is placed by the depth projection in depth.ts.
 */
export class WorldView {
  private readonly far: Phaser.GameObjects.TileSprite;
  private readonly mid: Phaser.GameObjects.TileSprite;
  /** Multiply tints that haze sprites in farther lanes, matching the baked lane art. */
  private readonly laneTint: readonly number[];
  private readonly foreground: Phaser.GameObjects.TileSprite;
  private readonly lanes: Phaser.GameObjects.Container[] = [];
  /** Dust rising in each lane's vents, redrawn every frame. */
  private readonly ventDust: Phaser.GameObjects.Graphics[] = [];
  /** Per lane: the radiation zones' glow and motes (drawn each frame, it flickers). */
  private readonly zoneGfx: Phaser.GameObjects.Graphics[] = [];
  private readonly zones: readonly ZoneRun[];
  private readonly zoneOutline: readonly ZoneEdge[];
  private readonly dustColor: number;
  private readonly player: Phaser.GameObjects.Sprite;
  private readonly enemySprites = new Map<number, Phaser.GameObjects.Sprite>();
  /** The lane each enemy sprite's container belongs to (the Ranger hops between them). */
  private readonly enemyLanes = new Map<number, number>();
  private readonly dazeSprites = new Map<number, Phaser.GameObjects.Sprite>();
  private readonly boltSprites = new Map<number, Phaser.GameObjects.Sprite>();
  /** The glowing doorways: hidden while a boss still holds the room. */
  private readonly exitParts: Phaser.GameObjects.Rectangle[] = [];
  private readonly cellSprites = new Map<number, Phaser.GameObjects.Sprite>();
  private readonly shardSprites = new Map<number, Phaser.GameObjects.Sprite>();
  private readonly shotSprites = new Map<number, Phaser.GameObjects.Sprite>();
  private readonly cacheSprites = new Map<number, Phaser.GameObjects.Sprite>();

  private camX: number;
  private camY: number;
  private camZ: number;
  private playerZ: number;
  private prevX = 0;
  private prevY = 0;
  private runDistance = 0;
  private bumpTicks = 0;
  private bumpDir = 0;
  private lastGhostTick = -1;
  private width: number;
  private height: number;
  private elapsed = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sim: Simulation,
    biome: Biome,
  ) {
    this.laneTint = LANE_HAZE.map((haze) => hexNumber(mix('#ffffff', biome.haze, haze)));
    this.dustColor = hexNumber(biome.ground.colors.topLit);
    this.width = scene.scale.width;
    this.height = scene.scale.height;
    // The sky never moves: everything else scrolls past it.
    scene.add.image(0, 0, TEX.sky).setOrigin(0).setDepth(ORDER.sky);
    this.far = scene.add
      .tileSprite(0, 0, this.width, SCENERY_HEIGHT, TEX.far)
      .setOrigin(0)
      .setDepth(ORDER.far);
    this.mid = scene.add
      .tileSprite(0, 0, this.width, SCENERY_HEIGHT, TEX.mid)
      .setOrigin(0)
      .setDepth(ORDER.mid);
    for (let i = 0; i < LANE_COUNT; i++) {
      const lane = scene.add.container(0, 0).setDepth(laneOrder(i));
      lane.add(scene.add.image(-LANE_PAD_X, 0, TEX.lane(i)).setOrigin(0));
      const dust = scene.add.graphics();
      lane.add(dust);
      this.ventDust.push(dust);
      const zone = scene.add.graphics();
      lane.add(zone);
      this.zoneGfx.push(zone);
      this.lanes.push(lane);
    }
    this.zones = zoneRuns(sim);
    this.zoneOutline = zoneEdges(sim);
    this.drawExits();
    this.player = scene.add.sprite(0, 0, TEX.player, PLAYER_FRAME.stand).setOrigin(0.5, 1);
    this.foreground = scene.add
      .tileSprite(0, 0, this.width, SCENERY_HEIGHT, TEX.foreground)
      .setOrigin(0)
      .setDepth(ORDER.foreground);

    const p = sim.player;
    this.camX = this.cameraTargetX();
    this.camY = this.cameraTargetY();
    this.camZ = p.lane;
    this.playerZ = p.lane;
    this.capture();
  }

  /** Each lane's exit tiles become one glowing doorway that pulses. */
  private drawExits(): void {
    for (let lane = 0; lane < LANE_COUNT; lane++) {
      const tiles = this.sim.room.exits.filter((e) => e.lane === lane);
      if (tiles.length === 0) continue;
      const x0 = Math.min(...tiles.map((t) => t.tx)) * TILE;
      const y0 = Math.min(...tiles.map((t) => t.ty)) * TILE;
      const x1 = (Math.max(...tiles.map((t) => t.tx)) + 1) * TILE;
      const y1 = (Math.max(...tiles.map((t) => t.ty)) + 1) * TILE;
      const glow = this.scene.add
        .rectangle(x0, y0, x1 - x0, y1 - y0, 0x7df9ff, 0.35)
        .setOrigin(0)
        .setStrokeStyle(2, 0xe8fff0, 0.9);
      const core = this.scene.add
        .rectangle(x0 + 4, y0 + 4, x1 - x0 - 8, y1 - y0 - 4, 0xe8fff0, 0.5)
        .setOrigin(0);
      this.lanes[lane]?.add([glow, core]);
      this.exitParts.push(glow, core);
      this.scene.tweens.add({
        targets: [glow, core],
        alpha: { from: 1, to: 0.45 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    for (const layer of [this.far, this.mid, this.foreground]) layer.setSize(width, SCENERY_HEIGHT);
  }

  /** Call before each sim step: remembers where the player was, for smooth in-between frames. */
  capture(): void {
    this.prevX = this.sim.player.x;
    this.prevY = this.sim.player.y;
  }

  /** A lane switch was refused: nudge the player toward the wall it hit. */
  bump(towardFarther: boolean): void {
    this.bumpTicks = 8;
    this.bumpDir = towardFarther ? -1 : 1;
  }

  /** Small pixels flying out of a point in a lane (enemy killed, pickup). */
  burst(lane: number, x: number, y: number, color: string): void {
    const container = this.lanes[lane];
    if (!container) return;
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const bit = this.scene.add.rectangle(x, y, 2, 2, hexNumber(color));
      container.add(bit);
      this.scene.tweens.add({
        targets: bit,
        x: x + Math.cos(angle) * 14,
        y: y + Math.sin(angle) * 14,
        alpha: 0,
        duration: 320,
        ease: 'Cubic.easeOut',
        onComplete: () => bit.destroy(),
      });
    }
  }

  /** Draws one frame. `alpha` is the progress between the last two sim ticks. */
  render(dtSeconds: number, alpha: number): void {
    this.elapsed += dtSeconds;
    const p = this.sim.player;
    // Interpolate the player between ticks, unless it teleported (respawn, rewind).
    const jumped = Math.abs(p.x - this.prevX) > 32 || Math.abs(p.y - this.prevY) > 32;
    const px = jumped ? p.x : this.prevX + (p.x - this.prevX) * alpha;
    const py = jumped ? p.y : this.prevY + (p.y - this.prevY) * alpha;

    this.playerZ = ease(this.playerZ, p.lane, 16, dtSeconds);
    this.camZ = ease(this.camZ, p.lane, 10, dtSeconds);
    this.camX = ease(this.camX, this.cameraTargetX(px), 7, dtSeconds);
    this.camY = ease(this.camY, this.cameraTargetY(py), 6, dtSeconds);
    if (jumped) {
      this.camX = this.cameraTargetX(px);
      this.camY = this.cameraTargetY(py);
    }

    this.placeScenery();
    this.placeLanes();
    this.placePlayer(px, py, dtSeconds);
    this.syncEntities();
    this.drawVentDust();
    this.drawZones();
    const open = !this.sim.bossAlive();
    for (const part of this.exitParts) part.setVisible(open);
  }

  private cameraTargetX(px = this.sim.player.x): number {
    const p = this.sim.player;
    const lookAhead = p.facing * 28;
    const half = this.width / 2;
    return Math.min(this.sim.room.width - half, Math.max(half, px + p.w / 2 + lookAhead));
  }

  /**
   * Follows the player up when it flies above the room; otherwise rests on the ground. Under a
   * ceiling (the tutorial) it stops at the room's top, so the lid is the top of the screen.
   */
  private cameraTargetY(py = this.sim.player.y): number {
    const resting = this.sim.room.height - this.height / 2;
    const y = Math.min(resting, py + 40);
    return this.sim.room.ceiling ? Math.max(this.height / 2, y) : y;
  }

  private placeScenery(): void {
    const restingY = this.sim.room.height - this.height / 2;
    const rise = restingY - this.camY;
    const layer = (sprite: Phaser.GameObjects.TileSprite, z: number) => {
      const s = depthScale(z, this.camZ);
      sprite.tilePositionX = Math.round(this.camX * s);
      sprite.y = Math.round(this.height - SCENERY_HEIGHT + rise * s);
    };
    layer(this.far, DEPTH.far);
    layer(this.mid, DEPTH.mid);
    layer(this.foreground, DEPTH.foreground);
    // The foreground thins out as the camera moves deeper, so it never hides the player.
    this.foreground.setAlpha(Math.max(0.25, 0.95 - this.camZ * 0.35));
  }

  private placeLanes(): void {
    this.lanes.forEach((lane, i) => {
      const s = depthScale(i, this.camZ);
      lane.setScale(s);
      lane.setPosition(
        Math.round(this.width / 2 - this.camX * s),
        Math.round(this.height / 2 - this.camY * s),
      );
      lane.setAlpha(laneAlpha(i, this.camZ));
    });
  }

  private placePlayer(px: number, py: number, dt: number): void {
    const p = this.sim.player;
    const s = depthScale(this.playerZ, this.camZ);
    let bump = 0;
    if (this.bumpTicks > 0) {
      this.bumpTicks = Math.max(0, this.bumpTicks - dt * 60);
      bump = Math.sin(this.bumpTicks * 1.6) * 2 * this.bumpDir;
    }
    this.player.setScale(s);
    this.player.setPosition(
      Math.round(this.width / 2 + (px + p.w / 2 - this.camX) * s),
      Math.round(this.height / 2 + (py + p.h - this.camY) * s + bump),
    );
    this.player.setDepth(laneOrder(this.playerZ) + 5);
    this.player.setFlipX(p.facing < 0);
    this.player.setVisible(p.invuln === 0 || Math.floor(p.invuln / 4) % 2 === 0);

    let frame: number = PLAYER_FRAME.stand;
    if (!p.grounded) {
      if (p.jetting) frame = this.sim.tick % 6 < 3 ? PLAYER_FRAME.jetA : PLAYER_FRAME.jetB;
      else if (p.fluttering) frame = PLAYER_FRAME.flutter;
      else frame = PLAYER_FRAME.air;
    } else if (Math.abs(p.vx) > 0.2) {
      this.runDistance += Math.abs(p.vx) * dt * 60;
      const cycle = [
        PLAYER_FRAME.stride,
        PLAYER_FRAME.pass,
        PLAYER_FRAME.stride2,
        PLAYER_FRAME.pass,
      ];
      frame = cycle[Math.floor(this.runDistance / 7) % cycle.length] ?? PLAYER_FRAME.stand;
    }
    this.player.setFrame(frame);

    // A charged rocket makes the suit pulse orange, radiation flickers it green; a dash leaves
    // fading afterimages.
    if (this.sim.rocketCharged() && Math.floor(this.elapsed * 10) % 2 === 0) {
      this.player.setTint(0xff8a24).setTintMode(Phaser.TintModes.FILL);
    } else if (p.exposed && Math.floor(this.elapsed * 8) % 3 === 0) {
      this.player.setTint(ZONE_COLOR.hot).setTintMode(Phaser.TintModes.FILL);
    } else {
      this.player.clearTint();
    }
    if (p.dashTicks > 0 && this.sim.tick !== this.lastGhostTick && this.sim.tick % 2 === 0) {
      this.lastGhostTick = this.sim.tick;
      const ghost = this.scene.add
        .sprite(this.player.x, this.player.y, TEX.player, frame)
        .setOrigin(0.5, 1)
        .setScale(s)
        .setFlipX(this.player.flipX)
        .setDepth(this.player.depth - 1)
        .setAlpha(0.5)
        .setTint(0x7df9ff)
        .setTintMode(Phaser.TintModes.FILL);
      this.scene.tweens.add({
        targets: ghost,
        alpha: 0,
        duration: 220,
        onComplete: () => ghost.destroy(),
      });
    }
  }

  /** A rocket's burst: a ring the size of the blast radius, and sparks. */
  blast(lane: number, x: number, y: number, radius: number): void {
    const container = this.lanes[lane];
    if (!container) return;
    const ring = this.scene.add.circle(x, y, radius, 0xffd23f, 0.35).setStrokeStyle(2, 0xff8a24);
    ring.setScale(0.3);
    container.add(ring);
    this.scene.tweens.add({
      targets: ring,
      scale: 1,
      alpha: 0,
      duration: 260,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
    this.burst(lane, x, y, '#ff8a24');
  }

  private syncEntities(): void {
    const seen = new Set<number>();
    for (const e of this.sim.enemies) {
      if (!e.alive) continue;
      seen.add(e.id);
      this.placeEnemy(e);
    }
    this.prune(this.enemySprites, seen);
    this.prune(this.dazeSprites, seen);
    for (const id of this.enemyLanes.keys()) if (!seen.has(id)) this.enemyLanes.delete(id);

    seen.clear();
    for (const c of this.sim.cells) {
      if (c.taken) continue;
      seen.add(c.id);
      let sprite = this.cellSprites.get(c.id);
      if (!sprite) {
        sprite = this.scene.add.sprite(0, 0, TEX.cell, 0);
        this.lanes[c.lane]?.add(sprite);
        this.cellSprites.set(c.id, sprite);
      }
      const bob = Math.round(Math.sin(this.elapsed * 3 + c.id) * 2);
      sprite.setPosition(Math.round(c.x), Math.round(c.y) + bob);
      sprite.setFrame(Math.floor(this.elapsed * 3 + c.id) % 2);
    }
    this.prune(this.cellSprites, seen);

    seen.clear();
    for (const sh of this.sim.shards) {
      if (sh.taken) continue;
      seen.add(sh.id);
      let sprite = this.shardSprites.get(sh.id);
      if (!sprite) {
        sprite = this.scene.add.sprite(0, 0, TEX.shard, 0);
        this.lanes[sh.lane]?.add(sprite);
        this.shardSprites.set(sh.id, sprite);
      }
      const bob = Math.round(Math.sin(this.elapsed * 2 + sh.id) * 2);
      sprite.setPosition(Math.round(sh.x), Math.round(sh.y) + bob);
      sprite.setFrame(Math.floor(this.elapsed * 1.5 + sh.id) % 2);
    }
    this.prune(this.shardSprites, seen);

    seen.clear();
    for (const c of this.sim.caches) {
      if (c.taken) continue;
      seen.add(c.id);
      let sprite = this.cacheSprites.get(c.id);
      if (!sprite) {
        sprite = this.scene.add.sprite(0, 0, TEX.cache, 0);
        this.lanes[c.lane]?.add(sprite);
        this.cacheSprites.set(c.id, sprite);
      }
      const bob = Math.round(Math.sin(this.elapsed * 2.5 + c.id) * 1.5);
      sprite.setPosition(Math.round(c.x), Math.round(c.y) + bob);
      sprite.setFrame(Math.floor(this.elapsed * 4) % 2);
    }
    this.prune(this.cacheSprites, seen);

    seen.clear();
    for (const s of this.sim.shots) {
      seen.add(s.id);
      let sprite = this.shotSprites.get(s.id);
      if (!sprite) {
        const tex = s.kind === 'rocket' ? TEX.rocket : s.pierce ? TEX.beam : TEX.laser;
        sprite = this.scene.add.sprite(0, 0, tex, 0);
        this.lanes[s.lane]?.add(sprite);
        this.shotSprites.set(s.id, sprite);
      }
      sprite.setPosition(Math.round(s.x), Math.round(s.y));
      sprite.setRotation(Math.atan2(s.vy, s.vx));
      if (s.kind === 'rocket') sprite.setFrame(Math.floor(this.sim.tick / 3) % 2);
    }
    this.prune(this.shotSprites, seen);

    seen.clear();
    for (const b of this.sim.bolts) {
      seen.add(b.id);
      let sprite = this.boltSprites.get(b.id);
      if (!sprite) {
        sprite = this.scene.add.sprite(0, 0, TEX.bolt, 0);
        this.lanes[b.lane]?.add(sprite);
        this.boltSprites.set(b.id, sprite);
      }
      sprite.setPosition(Math.round(b.x), Math.round(b.y));
      sprite.setFrame(Math.floor(this.sim.tick / 4) % 2);
    }
    this.prune(this.boltSprites, seen);
  }

  private placeEnemy(e: Enemy): void {
    let sprite = this.enemySprites.get(e.id);
    if (!sprite) {
      sprite = this.scene.add.sprite(0, 0, ENEMY_TEXTURE[e.kind], 0).setOrigin(0.5, 1);
      this.enemySprites.set(e.id, sprite);
    }
    // Hopped lanes: move it (and its daze stars) into the new lane's layer.
    if (this.enemyLanes.get(e.id) !== e.lane) {
      this.lanes[e.lane]?.add(sprite);
      const daze = this.dazeSprites.get(e.id);
      if (daze) this.lanes[e.lane]?.add(daze);
      this.enemyLanes.set(e.id, e.lane);
    }
    sprite.setPosition(Math.round(e.x + e.w / 2), Math.round(e.y + e.h));

    const tick = this.sim.tick;
    let flash: number | null = e.hitFlash > 0 ? 0xffffff : null;
    if (e.kind === 'crawler') {
      sprite.setFlipX(e.dir > 0);
      sprite.setFrame(Math.floor(tick / 8) % 2);
    } else if (e.kind === 'drone') {
      const aiming = e.mode === 'aim' && (e.timer > 24 || Math.floor(tick / 4) % 2 === 0);
      sprite.setFrame(
        aiming ? DRONE_FRAME.aim : Math.floor(tick / 3) % 2 ? DRONE_FRAME.idleB : DRONE_FRAME.idleA,
      );
    } else {
      sprite.setFlipX(e.dir < 0);
      let frame: number = PLAYER_FRAME.stand;
      if (Math.abs(e.vy) > 0.5) frame = PLAYER_FRAME.air;
      else if (e.mode === 'stalk' && Math.abs(e.vx) > 0)
        frame = RUN_CYCLE[Math.floor(tick / 6) % 4] ?? frame;
      else if (e.mode === 'charge') frame = RUN_CYCLE[Math.floor(tick / 3) % 4] ?? frame;
      sprite.setFrame(frame);
      // The tell: it flashes red before it hops in.
      if (flash === null && e.mode === 'tell' && Math.floor(tick / 4) % 2 === 0) flash = 0xff2a4a;
      this.placeDaze(e);
    }
    if (flash !== null) sprite.setTint(flash).setTintMode(Phaser.TintModes.FILL);
    else sprite.setTint(this.laneTint[e.lane] ?? 0xffffff).setTintMode(Phaser.TintModes.MULTIPLY);
  }

  /**
   * Each vent's updraft: a faint glow over the whole column, so its reach reads at a glance, and
   * specks of dust streaming up it, fading as they rise.
   */
  private drawVentDust(): void {
    for (const g of this.ventDust) g.clear();
    for (const v of this.sim.room.vents) {
      const g = this.ventDust[v.lane];
      if (!g) continue;
      const bottom = (v.bottom + 1) * TILE - 4;
      const top = v.top * TILE;
      const span = bottom - top;
      g.fillStyle(this.dustColor, 0.1).fillRect(v.tx * TILE, top, TILE, span);
      for (let k = 0; k < 10; k++) {
        const t = (this.elapsed * 0.8 + k / 10 + v.tx * 0.37) % 1;
        const sway = Math.round(Math.sin(this.elapsed * 3 + k * 1.7) * 2);
        const x = v.tx * TILE + 2 + ((k * 7) % 11) + sway;
        const size = k % 3 === 0 ? 2 : 1;
        g.fillStyle(this.dustColor, 0.9 * (1 - t));
        g.fillRect(x, Math.round(bottom - t * span), size, size);
      }
    }
  }

  /**
   * Radiation zones: a glow that breathes, motes drifting up through it, and a dashed outline that
   * marches round its open sides (hazard tape). Green where the suit isn't shielded for the tier,
   * a calm cyan where it is.
   */
  private drawZones(): void {
    for (const g of this.zoneGfx) g.clear();
    if (this.zones.length === 0) return;
    const safe = this.sim.room.radiation <= this.sim.suit.shielding;
    const color = safe ? ZONE_COLOR.safe : ZONE_COLOR.hot;
    const glow = (safe ? 0.07 : 0.13) + Math.sin(this.elapsed * 2.4) * 0.03;
    for (const z of this.zones) {
      const g = this.zoneGfx[z.lane];
      if (!g) continue;
      const x = z.tx * TILE;
      const y = z.ty * TILE;
      const w = z.length * TILE;
      g.fillStyle(color, glow).fillRect(x, y, w, TILE);
      // Two motes per tile, rising a tile and fading, each on its own phase.
      for (let k = 0; k < z.length * 2; k++) {
        const t = (this.elapsed * 0.6 + ((k * 37 + z.ty * 11 + z.tx * 7) % 100) / 100) % 1;
        const sway = Math.round(Math.sin(this.elapsed * 2 + k) * 2);
        const mx = x + ((((k * 8 + sway) % w) + w) % w);
        g.fillStyle(color, (safe ? 0.35 : 0.75) * (1 - t));
        g.fillRect(mx, Math.round(y + TILE - t * TILE), 1, 1);
      }
    }
    // Dashes 3 px long every 6 px, marching a pixel every tenth of a second.
    const march = Math.floor(this.elapsed * 10) % 6;
    for (const e of this.zoneOutline) {
      const g = this.zoneGfx[e.lane];
      if (!g) continue;
      g.fillStyle(color, safe ? 0.4 : 0.7);
      for (let d = -march; d < TILE; d += 6) {
        const from = Math.max(0, d);
        const to = Math.min(TILE, d + 3);
        if (to <= from) continue;
        if (e.horizontal) g.fillRect(e.x + from, e.y, to - from, 1);
        else g.fillRect(e.x, e.y + from, 1, to - from);
      }
    }
  }

  /** Stars circling a dazed boss: the opening to shoot it. */
  private placeDaze(e: Enemy): void {
    let daze = this.dazeSprites.get(e.id);
    if (!daze) {
      daze = this.scene.add.sprite(0, 0, TEX.daze, 0).setOrigin(0.5, 1);
      this.lanes[e.lane]?.add(daze);
      this.dazeSprites.set(e.id, daze);
    }
    daze.setVisible(e.mode === 'recover');
    daze.setPosition(Math.round(e.x + e.w / 2), Math.round(e.y - 3));
    daze.setFrame(Math.floor(this.sim.tick / 8) % 2);
  }

  private prune<T extends Phaser.GameObjects.GameObject>(
    map: Map<number, T>,
    keep: Set<number>,
  ): void {
    for (const [id, obj] of map) {
      if (!keep.has(id)) {
        obj.destroy();
        map.delete(id);
      }
    }
  }
}
